import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Calendar, User, ArrowLeft, Clock, BookOpen, Share2 } from 'lucide-react';
import { blogPosts } from '../data/blogData';

const PageHero = ({ eyebrow, title, highlight, sub }) => (
    <section style={{ background: 'linear-gradient(135deg,#0a1628 0%,#003366 100%)', paddingTop: '160px', paddingBottom: '60px' }}>
        <Container className="text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <span className="tat-eyebrow mb-4 d-inline-block">{eyebrow}</span>
                <h1 className="display-4 fw-bold text-white mt-3 mb-3">{title} <span style={{ color: '#00aeff' }}>{highlight}</span></h1>
                <p className="text-white opacity-75 mx-auto" style={{ maxWidth: '600px', fontSize: '1.1rem' }}>{sub}</p>
            </motion.div>
        </Container>
    </section>
);

export default function BlogPage() {
    const [selectedPost, setSelectedPost] = useState(null);

    // Scroll to top when opening an article
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [selectedPost]);

    const handleReadArticle = (post) => {
        setSelectedPost(post);
    };

    const handleBackToBlog = () => {
        setSelectedPost(null);
    };

    if (selectedPost) {
        // Find other suggested posts (excluding current one)
        const suggestions = blogPosts
            .filter(p => p.id !== selectedPost.id)
            .slice(0, 3);

        return (
            <AnimatePresence mode="wait">
                <motion.div
                    key="detail"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.4 }}
                    style={{ paddingTop: '100px', background: '#f8fafc' }}
                >
                    {/* Header Banner */}
                    <div style={{ background: 'linear-gradient(135deg,#0a1628 0%,#002244 100%)', color: '#fff', padding: '60px 0 40px 0' }}>
                        <Container>
                            <button 
                                onClick={handleBackToBlog}
                                className="btn btn-link text-white text-decoration-none p-0 d-inline-flex align-items-center gap-2 mb-4 hover-opacity-75"
                                style={{ fontSize: '0.95rem', fontWeight: 600, transition: 'all 0.2s' }}
                            >
                                <ArrowLeft size={18} /> Back to Blog
                            </button>
                            <div className="mb-3">
                                <span className="badge rounded-pill" style={{ background: '#00aeff', color: '#fff', fontSize: '0.8rem', padding: '0.5rem 1rem' }}>
                                    {selectedPost.category}
                                </span>
                            </div>
                            <h1 className="display-5 fw-bold mb-4" style={{ lineHeight: '1.3', maxWidth: '900px' }}>{selectedPost.title}</h1>
                            <div className="d-flex flex-wrap align-items-center gap-4 text-white-50" style={{ fontSize: '0.9rem' }}>
                                <span className="d-flex align-items-center gap-2"><Calendar size={16} /> {selectedPost.date}</span>
                                <span className="d-flex align-items-center gap-2"><User size={16} /> {selectedPost.author}</span>
                                <span className="d-flex align-items-center gap-2"><Clock size={16} /> 5 min read</span>
                            </div>
                        </Container>
                    </div>

                    {/* Main Content Area */}
                    <section className="py-5">
                        <Container>
                            <Row className="g-5">
                                {/* Article Content */}
                                <Col lg={8}>
                                    <div className="bg-white p-4 p-md-5 rounded-4 shadow-sm border border-light mb-5">
                                        {/* Featured Image */}
                                        <div className="rounded-4 overflow-hidden mb-5" style={{ height: '380px', border: '1px solid rgba(0,0,0,0.05)' }}>
                                            <img 
                                                src={selectedPost.image} 
                                                alt={selectedPost.title} 
                                                className="w-100 h-100 object-fit-cover" 
                                            />
                                        </div>

                                        {/* Body Content */}
                                        <div className="article-body">
                                            {selectedPost.content.map((block, idx) => {
                                                if (block.type === 'h2') {
                                                    return (
                                                        <h3 key={idx} className="fw-bold mt-5 mb-4" style={{ color: '#0a1628', fontSize: '1.65rem' }}>
                                                            {block.text}
                                                        </h3>
                                                    );
                                                } else if (block.type === 'p') {
                                                    return (
                                                        <p key={idx} className="text-muted mb-4" style={{ fontSize: '1.05rem', lineHeight: '1.8', color: '#4a5568' }}>
                                                            {block.text}
                                                        </p>
                                                    );
                                                } else if (block.type === 'ul') {
                                                    return (
                                                        <ul key={idx} className="ps-4 mb-4" style={{ listStyleType: 'disc' }}>
                                                            {block.items.map((item, itemIdx) => (
                                                                <li key={itemIdx} className="text-muted mb-3" style={{ fontSize: '1.05rem', lineHeight: '1.7', color: '#4a5568' }}>
                                                                    {item}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>

                                        <hr className="my-5" style={{ opacity: 0.15 }} />

                                        {/* Share / Footer */}
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <Share2 size={16} className="text-muted" />
                                                <span className="small text-muted fw-bold">Share this article:</span>
                                                <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1" style={{ fontSize: '0.8rem' }}>
                                                    Copy Link
                                                </button>
                                            </div>
                                            <button onClick={handleBackToBlog} className="btn btn-primary btn-premium px-4 py-2 border-0" style={{ fontSize: '0.9rem' }}>
                                                Back to All Blog Posts
                                            </button>
                                        </div>
                                    </div>
                                </Col>

                                {/* Sidebar suggestions */}
                                <Col lg={4}>
                                    <div className="position-sticky" style={{ top: '120px' }}>
                                        {/* CTA Card */}
                                        <Card className="border-0 rounded-4 p-4 text-white text-center mb-5" style={{ background: 'linear-gradient(135deg, #004a99 0%, #0077cc 100%)', boxShadow: '0 10px 30px rgba(0,74,153,0.15)' }}>
                                            <Card.Body className="p-2">
                                                <BookOpen size={40} className="mx-auto mb-3 opacity-90" />
                                                <h4 className="fw-bold mb-3">Partner with UltraRadX</h4>
                                                <p className="small opacity-90 mb-4 lh-lg">
                                                    Transform your radiology workflow with 24/7 subspecialty coverage, custom protocols, and rapid TAT commitments.
                                                </p>
                                                <a href="/contact" className="btn btn-light w-100 rounded-pill py-2 fw-bold text-primary shadow-sm" style={{ fontSize: '0.9rem' }}>
                                                    Schedule a Consultation
                                                </a>
                                            </Card.Body>
                                        </Card>

                                        {/* Suggested Reads */}
                                        <h5 className="fw-bold mb-4" style={{ color: '#0a1628' }}>Related Articles</h5>
                                        <div className="d-flex flex-column gap-4">
                                            {suggestions.map(post => (
                                                <div 
                                                    key={post.id} 
                                                    className="d-flex gap-3 align-items-start p-2 rounded-3 hover-bg-white transition cursor-pointer"
                                                    onClick={() => handleReadArticle(post)}
                                                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                >
                                                    <img 
                                                        src={post.image} 
                                                        alt={post.title} 
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }} 
                                                    />
                                                    <div>
                                                        <span className="text-primary fw-bold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            {post.category}
                                                        </span>
                                                        <h6 className="fw-bold mb-1 text-clamp-2 mt-1" style={{ color: '#0a1628', fontSize: '0.92rem', lineHeight: '1.4' }}>
                                                            {post.title}
                                                        </h6>
                                                        <span className="text-muted small">{post.date}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </section>
                </motion.div>
            </AnimatePresence>
        );
    }

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
            >
                <PageHero
                    eyebrow="INSIGHTS & NEWS"
                    title="UltraRadX"
                    highlight="Blog."
                    sub="Stay updated with the latest in teleradiology, clinical case studies, industry trends, and technological advancements."
                />

                <section className="py-5 section-padding bg-white">
                    <Container>
                        <Row className="g-4">
                            {blogPosts.map((post, i) => (
                                <Col lg={4} md={6} key={post.id}>
                                    <motion.div 
                                        initial={{ opacity: 0, y: 30 }} 
                                        whileInView={{ opacity: 1, y: 0 }} 
                                        viewport={{ once: true }} 
                                        transition={{ delay: i * 0.1 }} 
                                        className="h-100"
                                    >
                                        <Card className="h-100 border-0 rounded-4 overflow-hidden" style={{ boxShadow: '0 10px 30px rgba(0,74,153,0.08)', transition: 'transform 0.3s ease' }}>
                                            <div className="position-relative" style={{ height: '220px', overflow: 'hidden' }}>
                                                <div className="position-absolute top-0 start-0 m-3 z-1">
                                                    <span className="badge rounded-pill" style={{ background: '#00aeff', color: '#fff', fontSize: '0.75rem', padding: '0.4rem 0.8rem' }}>
                                                        {post.category}
                                                    </span>
                                                </div>
                                                <img 
                                                    src={post.image} 
                                                    alt={post.title} 
                                                    className="w-100 h-100 object-fit-cover transition" 
                                                    style={{ transition: 'transform 0.5s ease' }} 
                                                    onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'} 
                                                    onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'} 
                                                />
                                            </div>
                                            <Card.Body className="p-4 d-flex flex-column">
                                                <div className="d-flex align-items-center gap-3 mb-3 text-muted" style={{ fontSize: '0.85rem' }}>
                                                    <span className="d-flex align-items-center gap-1"><Calendar size={14} /> {post.date}</span>
                                                    <span className="d-flex align-items-center gap-1"><User size={14} /> {post.author}</span>
                                                </div>
                                                <h5 className="fw-bold mb-3" style={{ color: '#0a1628', lineHeight: '1.4' }}>{post.title}</h5>
                                                <p className="text-muted mb-4" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{post.excerpt}</p>
                                                <div className="mt-auto">
                                                    <button 
                                                        onClick={() => handleReadArticle(post)}
                                                        className="btn btn-link text-decoration-none p-0 fw-bold d-inline-flex align-items-center gap-1" 
                                                        style={{ color: '#004a99' }}
                                                    >
                                                        Read Article <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </motion.div>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            </motion.div>
        </AnimatePresence>
    );
}
